import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { Grid, Checkbox, FormControlLabel } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { useTotalWeight, useTotalCubes, useItemQuantity } from '@/hooks';
import { poundFormatter, integerFormatter } from '@/constants/formats';
import { Body1, Body2, ConfirmationBadge, GridItemRow as BaseRow, PlanningListItem } from '../blocks';
import { getOrderConfirmation } from '../util/orderConfirmation';
import { css } from '@emotion/react';
import { LOCATION_TYPES } from '@/constants/locationTypes';
import { colors } from '@/styles';
import { genAttributes, MARKETPLACE_TIERS } from '@onward-delivery/core';
import { useClientUser } from '@/hooks';
import { PRESCHEDULED_HIGHLIGHT } from '@/constants/featureFlags';
import { startCase } from 'lodash';
import { toNational } from '@/utilities/formatPhoneNumber';
import { toFullState } from '@/utilities/address';

// Keep in sync with the MARKETPLACE_TYPE filter in DispatchPlan/constants.js.
const MARKETPLACE_TIER_LIST = [MARKETPLACE_TIERS.T3_FULLY_MANAGED, MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING];

const GridItemRow = styled(BaseRow)`
    white-space: nowrap;
    flex: 0;
`;

const Badge = ({ color, ...props }) => (
    <Body2
        css={css`
            color: white;
            background-color: ${color || colors.greens.primary};
            font-size: 14px;
            font-weight: 700;
            padding: 0 1rem;
            border-radius: 10px;
            margin-right: 1rem;
        `}
        {...props}
    />
);

const OrderListItem = ({
    order,
    style,
    isSelected,
    assignedRouteColor,
    toggleSelectOrder,
    setActionableOrder,
    setModalOpen,
}) => {
    const { circles } = useClientUser();

    const itemQuantity = useItemQuantity([order]);
    const totalWeight = useTotalWeight([order]);
    const totalCubes = useTotalCubes([order]);
    const isMarketplace = !order.oms && MARKETPLACE_TIER_LIST.includes(order?.shipper_rate_override?.marketplace_tier);

    const display = useMemo(() => {
        if (!order) {
            return {};
        }

        const { street, unit, city, state, zip, location_type, location_info } = genAttributes(order);
        const {
            street: pu_street,
            unit: pu_unit,
            city: pu_city,
            state: pu_state,
            zip: pu_zip,
            location_type: pu_location_type,
            location_info: pu_location_info,
        } = genAttributes(order, true);

        const store = {
            address1: [order[pu_street], order[pu_unit]].filter((x) => x).join(', '),
            address2: `${order[pu_city]}, ${toFullState(order[pu_state])}  ${order[pu_zip]}`,
            type: order[pu_location_type] || order[pu_location_info],
        };
        const customer = {
            address1: [order[street], order[unit]].filter((x) => x).join(', '),
            address2: `${order[city]}, ${toFullState(order[state])}  ${order[zip]}`,
            type: order[location_type] || order[location_info],
        };

        return {
            ...((order.crossdock_leg === 'pickup' && order.order_type !== 'return') ||
            (order.crossdock_leg === 'dropoff' && order.order_type === 'return')
                ? store
                : customer),
            customer: order.dropoff_name,
            orderType: startCase(order.order_type),
        };
    }, [order]);

    const formatPhone = (phone) => {
        try {
            return phone ? toNational(phone) : 'N/A';
        } catch (e) {
            return 'N/A';
        }
    };

    const handleOrderClick = (event) => {
        event.preventDefault(); // Prevent default behavior
        event.stopPropagation(); // Stop event propagation
        setModalOpen('ORDER_VIEW');
        setActionableOrder(order);
    };

    const handleCheckboxChange = (event) => {
        event.stopPropagation();
        toggleSelectOrder(`${order.order_id}_${order.crossdock_leg}`);
    };

    const handleItemClick = (event) => {
        // Check if the click is on the checkbox or the order number
        if (!event.target.closest('.MuiCheckbox-root') && !event.target.closest('.order-number-span')) {
            toggleSelectOrder(`${order.order_id}_${order.crossdock_leg}`);
        }
    };

    const isUnscheduled = useMemo(() => {
        return order.crossdock_leg === 'pickup' ? !order.pickup_date : !order.delivery_date;
    }, [order]);

    const confirmation = useMemo(() => getOrderConfirmation(order), [order]);

    return (
        <PlanningListItem style={style} hover="true" onClick={handleItemClick}>
            <Grid container>
                <FormControlLabel
                    css={css`
                        width: 100%;
                        margin-right: 0;
                        align-items: flex-start;
                        .MuiFormControlLabel-label {
                            flex-grow: 1;
                        }
                    `}
                    control={
                        <Checkbox
                            color="primary"
                            checked={isSelected}
                            onChange={handleCheckboxChange}
                            onClick={(e) => e.stopPropagation()}
                        />
                    }
                    label={
                        <Grid
                            container
                            css={css`
                                flex-direction: column;
                            `}
                            onClick={(e) => e.stopPropagation()} // Prevent clicks on the label from triggering item selection
                        >
                            <GridItemRow
                                css={css`
                                    justify-content: flex-start;
                                `}
                            >
                                {order._isMissed ? <Badge color={colors.blues.primary}>Missed</Badge> : null}
                                {isUnscheduled ? <Badge color={colors.reds.primary}>Unscheduled</Badge> : null}
                                {isMarketplace ? <Badge>Marketplace</Badge> : null}
                                {order.prescheduled && circles?.[PRESCHEDULED_HIGHLIGHT] ? (
                                    <Badge color={colors.golds.primary}>Prescheduled</Badge>
                                ) : null}
                            </GridItemRow>
                            <GridItemRow
                                css={css`
                                    flex: 1;
                                `}
                            >
                                <Body1>
                                    Order{' '}
                                    <span
                                        className="order-number-span"
                                        css={css`
                                            color: ${colors.greens.primary};
                                            text-decoration: underline;
                                            cursor: pointer;
                                        `}
                                        onClick={handleOrderClick}
                                    >
                                        {order.order_number}
                                    </span>
                                    {!!order.crossdock_leg && (
                                        <span
                                            css={css`
                                                font-style: italic;
                                                color: ${colors.greys.primary};
                                            `}
                                        >
                                            {order.crossdock_leg === 'pickup'
                                                ? ' (pickup -> cross-dock)'
                                                : ' (cross-dock -> delivery)'}
                                        </span>
                                    )}
                                    <Body2
                                        css={css`
                                            margin-left: 8px;
                                        `}
                                    >
                                        {display.orderType}
                                    </Body2>
                                </Body1>
                                {!!assignedRouteColor && (
                                    <CheckCircleIcon
                                        fontSize="small"
                                        css={css`
                                            color: ${assignedRouteColor};
                                        `}
                                    />
                                )}
                            </GridItemRow>
                            <GridItemRow>
                                <Body1>PO: {order.po_number}</Body1>
                            </GridItemRow>
                            {order.reference_id && (
                                <GridItemRow>
                                    <Body2> Reference: {order.reference_id}</Body2>
                                </GridItemRow>
                            )}
                            <GridItemRow>
                                <Body1
                                    css={css`
                                        white-space: pre-line;
                                        max-height: 42px;
                                    `}
                                >
                                    Consignee: {display.customer}
                                </Body1>
                            </GridItemRow>
                            <GridItemRow>
                                <Body2>{display.address1}</Body2>
                            </GridItemRow>
                            <GridItemRow>
                                <Body2
                                    css={css`
                                        white-space: pre-line;
                                        max-height: 42px;
                                    `}
                                >
                                    {display.address2}
                                </Body2>
                            </GridItemRow>
                            <GridItemRow>
                                <Body2>Phone: {formatPhone(order.dropoff_phone)}</Body2>
                            </GridItemRow>
                            <GridItemRow>
                                <Body2>
                                    {integerFormatter.format(itemQuantity)} Items /{' '}
                                    {integerFormatter.format(totalCubes)} cu ft / {poundFormatter.format(totalWeight)}
                                </Body2>
                            </GridItemRow>
                            <GridItemRow>
                                <Body2>{`Service Type: ${
                                    display.type ? LOCATION_TYPES[display.type] || display.type : '--'
                                }`}</Body2>
                            </GridItemRow>
                            <GridItemRow
                                css={css`
                                    justify-content: flex-start;
                                `}
                            >
                                <Body2
                                    css={css`
                                        margin-right: 8px;
                                    `}
                                >
                                    Confirmation:
                                </Body2>
                                <ConfirmationBadge
                                    status={confirmation.status}
                                    css={css`
                                        display: inline-block;
                                        font-size: 11.5px;
                                        font-weight: 600;
                                        padding: 2px 10px;
                                    `}
                                >
                                    {confirmation.label}
                                </ConfirmationBadge>
                            </GridItemRow>
                            {order.notes &&
                                order.notes.length > 0 &&
                                order.notes.some((note) => note.type === 'Delivery') && (
                                    <GridItemRow
                                        css={css`
                                            max-width: 310px;
                                            flex-direction: column;
                                            align-items: flex-start;
                                            white-space: normal;
                                        `}
                                    >
                                        {order.notes.map(
                                            (note, index) =>
                                                note.type === 'Delivery' && (
                                                    <Body2
                                                        key={index}
                                                        css={css`
                                                            max-width: 310px;
                                                            white-space: normal;
                                                            overflow-wrap: break-word;
                                                            max-height: 42px;
                                                            overflow: hidden;
                                                        `}
                                                    >
                                                        Comments: {note.note}
                                                    </Body2>
                                                )
                                        )}
                                    </GridItemRow>
                                )}
                        </Grid>
                    }
                />
            </Grid>
        </PlanningListItem>
    );
};

export default React.memo(OrderListItem);
